import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Box, Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, MenuItem, Paper, Stack, Tab, Tabs,
  TextField, Typography,
} from '@mui/material';
import { AccountBalanceWallet, Close, Download, Paid, PendingActions, Replay, RequestQuote } from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import StatCard from '../../components/ui/StatCard';
import { withSerialNo } from '../../components/ui/gridColumns';
import { formatMoney } from '../../utils/currency';
import { getApiErrorMessage } from '../../services/userService';
import {
  LedgerRow, PartnerReportRow, PayoutLogEntry, downloadPayoutCsv, payoutApi,
} from '../../services/business/payoutService';
import {
  EMPTY_FILTERS, FilterState, PayoutFilterBar, StatusChip, dateText, filterParams, personLabel, titleCase,
} from './payoutShared';

const LEDGER_TYPES = ['payout', 'commission', 'reward', 'cashback', 'topup', 'withdrawal', 'withdrawal_reversal', 'adjustment', 'admin_adjust', 'refund', 'transfer', 'payment'];

function Overview({ filters }: { filters: FilterState }) {
  const { from, to, userType } = filterParams(filters);
  const { data, error } = useQuery({ queryKey: ['payout-summary', { from, to, userType }], queryFn: () => payoutApi.summary({ from, to, userType }) });
  if (error) return <Alert severity="error">{getApiErrorMessage(error)}</Alert>;
  if (!data) return <Typography>Loading…</Typography>;
  const t = data.totals;
  return (
    <Stack gap={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Requested" value={formatMoney(t.requestedAmount)} hint={`${t.requests} requests`} icon={<RequestQuote />} /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Paid to partners" value={formatMoney(t.paidAmount)} hint={`${t.paidCount} payouts · fees ${formatMoney(t.feesCollected)}`} icon={<Paid />} color="#16a34a" /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard title="In progress" value={formatMoney(t.openAmount)} hint={`${t.openCount} open`} icon={<PendingActions />} color="#f59e0b" /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Returned to wallet" value={formatMoney(t.returnedAmount)} hint={`${t.returnedCount} rejected / failed / cancelled`} icon={<Replay />} color="#dc2626" /></Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1}>Daily withdrawals</Typography>
        <Box height={280}>
          <ResponsiveContainer>
            <BarChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip formatter={(v: number) => formatMoney(v)} />
              <Legend />
              <Bar dataKey="requested" name="Requested" fill="#94a3b8" />
              <Bar dataKey="paid" name="Paid" fill="#16a34a" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>By status</Typography>
            {data.byStatus.map((s) => (
              <Stack key={s.status} direction="row" justifyContent="space-between" py={0.5}>
                <StatusChip status={s.status} /><Typography variant="body2">{s.count} · {formatMoney(s.amount)}</Typography>
              </Stack>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>By partner type</Typography>
            {data.byUserType.map((u) => (
              <Stack key={u.userType} direction="row" justifyContent="space-between" py={0.5}>
                <Typography variant="body2">{titleCase(u.userType)} ({u.partners})</Typography>
                <Typography variant="body2">{formatMoney(u.paid)} paid of {formatMoney(u.requested)}</Typography>
              </Stack>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>Paid by route</Typography>
            {data.byGateway.map((g) => (
              <Stack key={`${g.gateway}-${g.automatic}`} direction="row" justifyContent="space-between" py={0.5}>
                <Typography variant="body2">{g.gateway === 'stripe' ? 'Stripe' : 'Manual bank'}{g.automatic ? ' (automatic)' : ''}</Typography>
                <Typography variant="body2">{g.count} · {formatMoney(g.net)}</Typography>
              </Stack>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
}

function StatementDialog({ userId, filters, onClose }: { userId: string; filters: FilterState; onClose: () => void }) {
  const { from, to } = filterParams(filters);
  const { data, error } = useQuery({ queryKey: ['payout-statement', userId, from, to], queryFn: () => payoutApi.statement(userId, { from, to }) });
  const cur = data?.currency;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box flex={1}>Statement — {data?.partner.name || ''}</Box>
        <Button startIcon={<Download />} onClick={() => downloadPayoutCsv(`/payouts/admin/reports/partners/${userId}`, { from, to }, 'partner-statement')}>CSV</Button>
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{getApiErrorMessage(error)}</Alert>}
        {!data ? <Typography>Loading…</Typography> : (
          <Stack gap={2}>
            <Typography color="text.secondary">
              {titleCase(data.partner.userType)} · {data.partner.phone} {data.partner.email} · KYC <StatusChip status={data.kyc.status} />
            </Typography>
            <Grid container spacing={2}>
              {[
                ['Opening balance', data.openingBalance], ['Credits', data.totalCredits], ['Debits', data.totalDebits],
                ['Closing balance', data.closingBalance], ['Current balance', data.currentBalance],
              ].map(([label, v]) => (
                <Grid item xs={6} md={2.4} key={label as string}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography fontWeight={800}>{formatMoney(v as number, cur)}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Stack direction="row" gap={2} flexWrap="wrap">
              {data.breakdown.map((b) => (
                <Typography key={b.type} variant="body2">
                  <b>{titleCase(b.type)}</b>: {b.credit ? `+${formatMoney(b.credit, cur)}` : ''} {b.debit ? `−${formatMoney(b.debit, cur)}` : ''} ({b.count})
                </Typography>
              ))}
            </Stack>
            {data.truncated && <Alert severity="info">Showing the first transactions only — narrow the date range or export CSV.</Alert>}
            <DataGrid
              rows={data.transactions}
              getRowId={(r) => r._id}
              autoHeight
              density="compact"
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              columns={[
                { field: 'createdAt', headerName: 'Date', width: 170, valueFormatter: (v) => dateText(v as string) },
                { field: 'type', headerName: 'Type', width: 150, valueFormatter: (v) => titleCase(v as string) },
                { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
                { field: 'credit', headerName: 'Credit', width: 110, valueGetter: (_v, r) => (r.amount > 0 ? formatMoney(r.amount, cur) : '') },
                { field: 'debit', headerName: 'Debit', width: 110, valueGetter: (_v, r) => (r.amount < 0 ? formatMoney(-r.amount, cur) : '') },
                { field: 'balanceAfter', headerName: 'Balance', width: 120, valueFormatter: (v) => formatMoney(v, cur) },
              ]}
            />
            <Typography variant="subtitle2" fontWeight={700}>Withdrawals in period</Typography>
            {data.withdrawals.length === 0 && <Typography variant="body2" color="text.secondary">None</Typography>}
            {data.withdrawals.map((w) => (
              <Stack key={w._id} direction="row" gap={1} alignItems="center">
                <StatusChip status={w.status} />
                <Typography variant="body2">{w.requestNumber} · {dateText(w.createdAt)} · {formatMoney(w.amount, w.currency)} (net {formatMoney(w.netAmount, w.currency)}) {w.transactionRef ? `· ref ${w.transactionRef}` : ''}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PartnerWise({ filters }: { filters: FilterState }) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState('earnings');
  const [statementFor, setStatementFor] = useState<string | null>(null);
  const params = { ...filterParams(filters), sort, page: page + 1, limit: pageSize };
  const { data, isLoading, error } = useQuery({ queryKey: ['payout-partner-report', params], queryFn: () => payoutApi.partnerReport(params) });
  const totals = data?.meta?.totals;

  const columns: GridColDef<PartnerReportRow>[] = useMemo(() => [
    { field: 'name', headerName: 'Partner', flex: 1, minWidth: 160 },
    { field: 'userType', headerName: 'Type', width: 120, valueFormatter: (v) => titleCase(v as string) },
    { field: 'kycStatus', headerName: 'KYC', width: 110, renderCell: ({ value }) => <StatusChip status={value as string} /> },
    { field: 'earnings', headerName: 'Earnings', width: 110, valueFormatter: (v, r) => formatMoney(v, r.currency) },
    { field: 'incentives', headerName: 'Incentives', width: 105, valueFormatter: (v, r) => formatMoney(v, r.currency) },
    { field: 'topups', headerName: 'Top-ups', width: 100, valueFormatter: (v, r) => formatMoney(v, r.currency) },
    { field: 'withdrawn', headerName: 'Withdrawn', width: 110, valueFormatter: (v, r) => formatMoney(v, r.currency) },
    { field: 'reversed', headerName: 'Returned', width: 100, valueFormatter: (v, r) => formatMoney(v, r.currency) },
    { field: 'paidNet', headerName: 'Paid to bank', width: 115, valueFormatter: (v, r) => formatMoney(v, r.currency) },
    { field: 'openAmount', headerName: 'In progress', width: 110, valueFormatter: (v, r) => formatMoney(v, r.currency) },
    { field: 'balance', headerName: 'Balance now', width: 115, valueFormatter: (v, r) => formatMoney(v, r.currency) },
    { field: 'txCount', headerName: 'Txns', width: 70 },
    { field: 'view', headerName: '', width: 110, sortable: false, renderCell: ({ row }) => <Button size="small" onClick={() => setStatementFor(row.userId)}>Statement</Button> },
  ], []);

  return (
    <>
      <Stack direction="row" gap={1} mb={1.5} alignItems="center" flexWrap="wrap">
        <TextField select size="small" label="Sort by" value={sort} onChange={(e) => setSort(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="earnings">Earnings</MenuItem>
          <MenuItem value="withdrawn">Paid to bank</MenuItem>
          <MenuItem value="balance">Wallet balance</MenuItem>
          <MenuItem value="name">Name</MenuItem>
        </TextField>
        <Box flex={1} />
        <Button startIcon={<Download />} variant="outlined" onClick={() => downloadPayoutCsv('/payouts/admin/reports/partners', params, 'partner-transactions')}>Export CSV</Button>
      </Stack>
      {totals && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          {data?.meta.total} partners · Earnings {formatMoney(totals.earnings)} · Incentives {formatMoney(totals.incentives)} · Top-ups {formatMoney(totals.topups)} ·
          Withdrawn {formatMoney(totals.withdrawn)} · Paid {formatMoney(totals.paidNet)} · Fees {formatMoney(totals.fees)} · Wallet balances {formatMoney(totals.balance)}
        </Typography>
      )}
      {error && <Alert severity="error">{getApiErrorMessage(error)}</Alert>}
      <DataGrid
        rows={data?.rows || []}
        columns={withSerialNo(columns)}
        loading={isLoading}
        getRowId={(r) => r.userId}
        autoHeight
        paginationMode="server"
        rowCount={data?.meta?.total ?? 0}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
        pageSizeOptions={[25, 50, 100]}
        onRowDoubleClick={({ row }) => setStatementFor(row.userId)}
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />
      {statementFor && <StatementDialog userId={statementFor} filters={filters} onClose={() => setStatementFor(null)} />}
    </>
  );
}

function Ledger({ filters }: { filters: FilterState }) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [type, setType] = useState('');
  const params = { ...filterParams(filters), type: type || undefined, page: page + 1, limit: pageSize };
  const { data, isLoading, error } = useQuery({ queryKey: ['payout-ledger', params], queryFn: () => payoutApi.ledger(params) });

  const columns: GridColDef<LedgerRow>[] = useMemo(() => [
    { field: 'createdAt', headerName: 'Date', width: 170, valueFormatter: (v) => dateText(v as string) },
    { field: 'user', headerName: 'Partner', flex: 1, minWidth: 150, valueGetter: (_v, r) => personLabel(r.user) },
    { field: 'userType', headerName: 'Partner type', width: 130, valueGetter: (_v, r) => titleCase(r.user?.userType) },
    { field: 'type', headerName: 'Type', width: 150, valueFormatter: (v) => titleCase(v as string) },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    { field: 'credit', headerName: 'Credit', width: 110, valueGetter: (_v, r) => (r.amount > 0 ? formatMoney(r.amount, r.currency) : '') },
    { field: 'debit', headerName: 'Debit', width: 110, valueGetter: (_v, r) => (r.amount < 0 ? formatMoney(-r.amount, r.currency) : '') },
    { field: 'balanceAfter', headerName: 'Balance', width: 120, valueFormatter: (v, r) => formatMoney(v, r.currency) },
  ], []);

  return (
    <>
      <Stack direction="row" gap={1} mb={1.5} alignItems="center" flexWrap="wrap">
        <TextField select size="small" label="Transaction type" value={type} onChange={(e) => { setType(e.target.value); setPage(0); }} sx={{ minWidth: 200 }}>
          <MenuItem value="">All types</MenuItem>
          {LEDGER_TYPES.map((t) => <MenuItem key={t} value={t}>{titleCase(t)}</MenuItem>)}
        </TextField>
        <Box flex={1} />
        <Button startIcon={<Download />} variant="outlined" onClick={() => downloadPayoutCsv('/payouts/admin/reports/ledger', params, 'partner-ledger')}>Export CSV</Button>
      </Stack>
      {data?.meta?.totals && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          {data.meta.total} transactions · Credits {formatMoney(data.meta.totals.credit)} · Debits {formatMoney(data.meta.totals.debit)}
        </Typography>
      )}
      {error && <Alert severity="error">{getApiErrorMessage(error)}</Alert>}
      <DataGrid
        rows={data?.rows || []}
        columns={withSerialNo(columns)}
        loading={isLoading}
        getRowId={(r) => r._id}
        autoHeight
        paginationMode="server"
        rowCount={data?.meta?.total ?? 0}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
        pageSizeOptions={[50, 100, 200]}
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />
    </>
  );
}

function ActivityLog({ filters }: { filters: FilterState }) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [entity, setEntity] = useState('');
  const [actorType, setActorType] = useState('');
  const { from, to } = filterParams(filters);
  const params = { from, to, entity: entity || undefined, actorType: actorType || undefined, action: filters.search.trim() || undefined, page: page + 1, limit: pageSize };
  const { data, isLoading, error } = useQuery({ queryKey: ['payout-logs', params], queryFn: () => payoutApi.logs(params) });

  const columns: GridColDef<PayoutLogEntry>[] = useMemo(() => [
    { field: 'createdAt', headerName: 'Time', width: 170, valueFormatter: (v) => dateText(v as string) },
    { field: 'action', headerName: 'Action', width: 220 },
    { field: 'actor', headerName: 'By', width: 160, valueGetter: (_v, r) => (r.actorId ? personLabel(r.actorId) : titleCase(r.actorType)) },
    { field: 'partner', headerName: 'Partner', width: 160, valueGetter: (_v, r) => personLabel(r.partnerId) },
    { field: 'change', headerName: 'Status', width: 170, valueGetter: (_v, r) => (r.fromStatus || r.toStatus ? `${r.fromStatus || '—'} → ${r.toStatus || '—'}` : '') },
    { field: 'amount', headerName: 'Amount', width: 110, valueFormatter: (v) => (v != null ? formatMoney(v) : '') },
    { field: 'message', headerName: 'Details', flex: 1, minWidth: 200 },
    { field: 'ip', headerName: 'IP', width: 130 },
  ], []);

  return (
    <>
      <Stack direction="row" gap={1} mb={1.5} alignItems="center" flexWrap="wrap">
        <TextField select size="small" label="Area" value={entity} onChange={(e) => { setEntity(e.target.value); setPage(0); }} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>
          {['withdrawal', 'kyc', 'account', 'settings', 'webhook'].map((e) => <MenuItem key={e} value={e}>{titleCase(e)}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Actor" value={actorType} onChange={(e) => { setActorType(e.target.value); setPage(0); }} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>
          {['partner', 'admin', 'system', 'gateway'].map((e) => <MenuItem key={e} value={e}>{titleCase(e)}</MenuItem>)}
        </TextField>
        <Box flex={1} />
        <Button startIcon={<Download />} variant="outlined" onClick={() => downloadPayoutCsv('/payouts/admin/logs', params, 'payout-logs')}>Export CSV</Button>
      </Stack>
      {error && <Alert severity="error">{getApiErrorMessage(error)}</Alert>}
      <DataGrid
        rows={data?.rows || []}
        columns={columns}
        loading={isLoading}
        getRowId={(r) => r._id}
        autoHeight
        paginationMode="server"
        rowCount={data?.meta?.total ?? 0}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
        pageSizeOptions={[50, 100, 200]}
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />
    </>
  );
}

export default function PayoutReportsPage() {
  const [tab, setTab] = useState(0);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  return (
    <PageShell>
      <PageHeader
        eyebrow="Finance"
        title="Payout Reports"
        subtitle="Withdrawal totals, partner-wise earnings and payouts, the full partner wallet ledger, and the payout audit log."
      />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab icon={<Paid />} iconPosition="start" label="Overview" />
        <Tab icon={<AccountBalanceWallet />} iconPosition="start" label="Partner-wise" />
        <Tab icon={<RequestQuote />} iconPosition="start" label="Ledger" />
        <Tab icon={<PendingActions />} iconPosition="start" label="Activity log" />
      </Tabs>
      <PayoutFilterBar value={filters} onChange={setFilters} hideSearch={tab === 0} />
      {tab === 0 && <Overview filters={filters} />}
      {tab === 1 && <PartnerWise filters={filters} />}
      {tab === 2 && <Ledger filters={filters} />}
      {tab === 3 && <ActivityLog filters={filters} />}
    </PageShell>
  );
}
